import MetricWidget from 'components/dashboard/MetricWidget';
import { SiPagespeedinsights } from 'react-icons/si';
import { BiErrorCircle, BiTimer, BiUser } from 'react-icons/bi';
import { GrSend } from 'react-icons/gr';
import { ChartsByLabel, PerformanceTest } from 'interfaces/performanceTest';
import { useEffect, useState } from 'react';
import { useToast } from 'hooks/useToast';
import ObservationRow from 'components/dashboard/ObservationRow';
import {
  DraftObservation,
  Observation,
  ReportObservations,
} from 'interfaces/observation';
import ObservationForm from 'components/dashboard/ObservationForm';
import {
  createObservation,
  getObservations,
  updateObservation,
} from 'services/observation';
import dayjs from 'dayjs';
import RequestCount from 'components/dashboard/charts/RequestCount';
import ResponseTime from 'components/dashboard/charts/ResponseTime';
import ReportSummaryStats from 'components/dashboard/tables/ReportSummaryStats';
import VirtualUsers from 'components/dashboard/charts/VirtualUsers';
import { useRouter } from 'next/router';
import { durationFormatter } from './charts/helpers';
import TestPlan from './tables/TestPlan';
import { getReportChartDataV1 } from 'services/report';

interface Props {
  perfReport: PerformanceTest;
}

const deriveTicks = (start: number, end: number): number[] => {
  const maxTicks = 10;
  // const defaultWindow = 5;
  const tickWindowOptions = [5, 15, 30, 60, 120, 180, 300, 600, 1800];
  const duration = end - start;

  // NOTE(bobsin): this finds the option where
  // duration / option is less than max ticks desired
  const possibleTickWindows = tickWindowOptions.filter(
    (tickWindow) => duration / tickWindow < maxTicks
  );
  const selectedTickWindow = possibleTickWindows[0];
  // TODO(bobsin): handle selectedTickWindow == undefined

  const ticks: number[] = [];
  // TODO(bobsin): change this to begin on the start of tickWindow
  let currentTick: number = start;

  // TODO(bobsin): improve safety of this while loop
  while (currentTick + selectedTickWindow <= end) {
    currentTick += selectedTickWindow;
    ticks.push(currentTick);
  }

  return ticks;
};

const isDemoReport = (query) => {
  const slugValue = (query.slug || [])[0];
  return slugValue === 'demo';
};

const PerformanceReport: React.FC<Props> = ({ perfReport }) => {
  const { addToast } = useToast();
  const { query } = useRouter();

  const [reportTimeRange, setReportTimeRange] = useState<number[]>([]);
  const [observations, setObservations] = useState<ReportObservations>({});
  const [ticks, setTicks] = useState<number[]>([]);
  const [chartsByLabel, setChartsByLabel] = useState<ChartsByLabel>(null);
  const [chartsLoading, setChartsLoading] = useState<boolean>(true);

  useEffect(() => {
    // TODO(bobsin); put this in a setTimeout to allow nested useEffect to run
    if (perfReport) {
      getObservations(perfReport.id).then(setObservations).catch(console.warn);
      if (!chartsByLabel) {
        // TODO(bobsin): improve error handling
        getReportChartDataV1(perfReport.id)
          .then((byLabel) => {
            setChartsByLabel(byLabel);

            const start = (byLabel['Overall'].timeStamps as any).at(0);
            const end = (byLabel['Overall'].timeStamps as any).at(-1);
            setReportTimeRange([start, end]);
            setTicks(deriveTicks(start, end));
            setChartsLoading(false);
          })
          .catch((error) => {
            addToast({
              title: 'Error loading charts',
              description: error.message,
              type: 'error',
            });
          });
      }
    }
  }, [addToast, perfReport, chartsByLabel]);

  const onSubmitConclusion = (cls: DraftObservation) => {
    createObservation({
      ...cls,
      reportId: perfReport.id,
    })
      .then((created) => {
        setObservations({
          ...observations,
          [created.subject]: [
            ...(observations[created.subject] || []),
            created,
          ],
        });
        addToast({
          title: 'Documented new conclusion',
          description: created.description,
          type: 'success',
        });
      })
      .catch(console.error);
  };

  const onChangeObservation = (observation: Observation) => {
    updateObservation({ reportId: perfReport.id, observation }).then(
      (updated) => {
        setObservations({
          ...observations,
          [updated.subject]: observations[updated.subject].map((obs) =>
            obs.id === updated.id ? updated : obs
          ),
        });
        addToast({
          title: 'Updated status',
          description: updated.description,
          type: 'success',
        });
      }
    );
  };

  return (
    <div>
      <div className="px-4 py-10 pb-12 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:max-w-full">
        <header className="pb-4 pl-3 mb-6 border-b-2 border-gray-100 sm:py-6">
          <div className="mt-2 md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-800 sm:text-3xl sm:leading-9 sm:truncate">
                {perfReport?.label}
              </h2>
            </div>

            <div className="text-lg text-gray-800">
              <div>
                {reportTimeRange[0]
                  ? dayjs
                      .unix(reportTimeRange[0])
                      .format('MMMM D YYYY, h:mm:ss a')
                  : ''}
              </div>
              <div>
                {reportTimeRange[1]
                  ? dayjs
                      .unix(reportTimeRange[1])
                      .format('MMMM D YYYY, h:mm:ss a')
                  : ''}
              </div>
            </div>
          </div>
        </header>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3 lg:grid-cols-6">
          <MetricWidget
            label="Requests"
            icon={<GrSend className="h-6 w-6" />}
            value={`${perfReport ? perfReport.metrics.totalRequests : 0}`}
          />
          <MetricWidget
            label="Failures"
            icon={<BiErrorCircle className="h-6 w-6 text-apricot-500" />}
            value={`${perfReport ? perfReport.metrics.totalFailures : 0}`}
          />
          <MetricWidget
            label="Test Duration"
            icon={<BiTimer className="h-6 w-6 text-new-orleans-600" />}
            value={
              reportTimeRange.length > 0
                ? durationFormatter(reportTimeRange)
                : ''
            }
          />
          <MetricWidget
            label="Virtual Users"
            icon={<BiUser className="h-6 w-6 text-summer-green-500" />}
            value={`${perfReport ? perfReport.metrics.maxVirtualUsers : 0}`}
          />
          <MetricWidget
            label="Average Latency"
            icon={
              <SiPagespeedinsights className="h-6 w-6 text-san-juan-blue-400" />
            }
            value={`${perfReport ? perfReport.metrics.latencies.avgMs : 0}ms`}
          />
          <MetricWidget
            label="p90 Latency"
            icon={
              <SiPagespeedinsights className="h-6 w-6 text-san-juan-blue-400" />
            }
            value={`${perfReport?.metrics?.latencies?.p90Ms || 0}ms`}
          />
        </div>
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-5">
          <div className="sm:col-span-3 overflow-hidden bg-white rounded-lg shadow-md">
            <TestPlan
              loading={chartsLoading}
              reportTimeRange={reportTimeRange}
              labels={Object.keys(chartsByLabel || {})}
            />
          </div>
          <div className="sm:col-span-2 overflow-hidden bg-white rounded-lg shadow-md">
            <div className="my-5 px-5 w-full text-lg text-gray-900">
              <header className="pl-3 font-semibold">Observations</header>
              <ul className="mt-5">
                {observations.conclusion?.map((obj, i) => {
                  return (
                    <li className="border-t-2 border-gray-100" key={i}>
                      <ObservationRow
                        observation={obj}
                        onChange={onChangeObservation}
                      />
                    </li>
                  );
                })}
                <li className="border-t-2 border-gray-100">
                  <ObservationForm
                    handleSubmit={onSubmitConclusion}
                    disableSubmit={isDemoReport(query)}
                    subject="conclusion"
                    subjectLabel="observation"
                  />
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="mt-8 overflow-hidden bg-white rounded-lg shadow-md">
          <div className="p-5 w-full overflow-x-scroll lg:overflow-x-hidden text-lg font-semibold text-gray-900">
            <ReportSummaryStats performanceTest={perfReport} />
          </div>
        </div>

        <div className="mt-8 overflow-hidden bg-white rounded-lg shadow-md">
          <div className="my-5 px-5 w-full text-lg font-semibold text-gray-900">
            <header className="pb-4 pl-3 border-b-2 border-gray-100">
              Total Requests
            </header>
            <RequestCount
              ticks={ticks}
              charts={chartsByLabel}
              loading={chartsLoading}
            />
            <ul className="mt-5">
              {observations.totalRequests?.map((obj, i) => {
                return (
                  <li className="border-t-2 border-gray-100" key={i}>
                    <ObservationRow
                      observation={obj}
                      onChange={onChangeObservation}
                    />
                  </li>
                );
              })}
              <li className="border-t-2 border-gray-100">
                <ObservationForm
                  handleSubmit={onSubmitConclusion}
                  disableSubmit={isDemoReport(query)}
                  subject="totalRequests"
                  subjectLabel="observation"
                />
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-8 overflow-hidden bg-white rounded-lg shadow-md">
          <div className="my-5 px-5 w-full text-lg font-semibold text-gray-900">
            <ResponseTime
              charts={chartsByLabel}
              ticks={ticks}
              loading={chartsLoading}
            />
            <ul className="mt-5">
              {observations.responseTime?.map((obj, i) => {
                return (
                  <li className="border-t-2 border-gray-100" key={i}>
                    <ObservationRow
                      observation={obj}
                      onChange={onChangeObservation}
                    />
                  </li>
                );
              })}
              <li className="border-t-2 border-gray-100">
                <ObservationForm
                  handleSubmit={onSubmitConclusion}
                  subject="responseTime"
                  subjectLabel="observation"
                />
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-8 overflow-hidden bg-white rounded-lg shadow-md">
          <div className="my-5 px-5 w-full text-lg font-semibold text-gray-900">
            <VirtualUsers
              charts={chartsByLabel}
              ticks={ticks}
              loading={chartsLoading}
            />
            <ul className="mt-5">
              {observations.virtualUsers?.map((obj, i) => {
                return (
                  <li className="border-t-2 border-gray-100" key={i}>
                    <ObservationRow
                      observation={obj}
                      onChange={onChangeObservation}
                    />
                  </li>
                );
              })}
              <li className="border-t-2 border-gray-100">
                <ObservationForm
                  handleSubmit={onSubmitConclusion}
                  subject="virtualUsers"
                  subjectLabel="observation"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceReport;
